import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  fundMilestone,
  submitWork,
  approveMilestone,
  requestChanges,
  releaseMilestone,
  disputeMilestone,
  invoiceFromMilestone,
  confirmFundMilestone,
  confirmClaimMilestone,
  remindFunding,
  proposeRevision,
  approveRevision,
  declineRevision,
  withdrawRevision,
  authorizeRevise,
  confirmRevise,
  cancelMilestone,
} from "@/services/contracts.service";
import { cancelDispute } from "@/services/disputes.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney, fmtDate, statusColor, milestoneStatusLabel } from "@/lib/format";
import { milestoneCaps } from "@/lib/permissions";
import { useWalletGuard } from "@/lib/useWalletGuard";
import { fundMilestoneOnchain, settleMilestoneOnchain, reviseMilestoneOnchain } from "@/lib/onchain";
import {
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  RotateCcw,
  CreditCard,
  Clock,
  FileSpreadsheet,
  XCircle,
  Loader2,
} from "lucide-react";
import ExpandableText from "@/components/shared/ExpandableText";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function pollConfirm(fn, id) {
  for (let i = 0; i < 30; i++) {
    const r = await fn(id);
    if (r?.status === "confirmed") return true;
    await sleep(2500);
  }
  return false;
}

const MILESTONE_ACTIONS = {
  submit: submitWork,
  approve: approveMilestone,
  "request-changes": requestChanges,
  dispute: disputeMilestone,
};

const MilestoneRow = ({ m, contract, user, reload }) => {
  const [submitOpen, setSubmitOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [changesOpen, setChangesOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [reviseAmount, setReviseAmount] = useState("");
  const [reviseNote, setReviseNote] = useState("");
  const [link, setLink] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [disputeAmt, setDisputeAmt] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState("Other");
  const [evidence, setEvidence] = useState("");

  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null);
  const [phase, setPhase] = useState(null);
  const ensureWallet = useWalletGuard();

  const isPayer = !!contract.is_payer;
  const isPayee = !!contract.is_payee;
  const caps = milestoneCaps(contract, m);

  const phaseLabel = { prep: "Preparing…", wallet: "Confirm in wallet…", confirm: "Confirming…" }[phase] || "Working…";
  const Busy = ({ label }) => (
    <>
      <Loader2 className="w-4 h-4 mr-1 animate-spin" /> {label || phaseLabel}
    </>
  );

  const act = async (path, body) => {
    try {
      await MILESTONE_ACTIONS[path](m.milestone_id, body || {});
      toast.success("Done");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const submitCancel = async () => {
    if (cancelReason.trim().length < 3) {
      toast.error("Please enter a reason");
      return;
    }
    setBusy(true);
    try {
      const res = await cancelMilestone(m.milestone_id, { reason: cancelReason.trim() });
      toast.success(
        res?.open_remaining === 0
          ? "Milestone cancelled — that was the last open milestone on this contract."
          : "Milestone cancelled."
      );
      setCancelOpen(false);
      setCancelReason("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setBusy(false);
    }
  };

  const fundOnchain = async () => {
    setBusy(true);
    setPhase("prep");
    try {
      await ensureWallet();
      toast.message("Preparing your funding transaction…");
      const proof = await fundMilestone(m.milestone_id);
      setPhase("wallet");
      await fundMilestoneOnchain(proof, ({ step: n, total, kind }) => {
        setStep(kind);
        toast.message(
          kind === "approve"
            ? `Step ${n} of ${total}: approve USDC spending in your wallet…`
            : total > 1
              ? `Step ${n} of ${total}: confirm the deposit in your wallet…`
              : "Confirm the deposit in your wallet…"
        );
      });
      setPhase("confirm");
      toast.message("Waiting for on-chain confirmation…");
      const ok = await pollConfirm(confirmFundMilestone, m.milestone_id);
      if (ok) {
        toast.success("Milestone funded");
        reload();
      } else {
        toast.error("Funding not confirmed yet, refresh shortly");
      }
    } catch (e) {
      toast.error(e.shortMessage || e.response?.data?.detail || e.message || "Funding failed");
    } finally {
      setBusy(false);
      setStep(null);
      setPhase(null);
    }
  };

  const claimOnchain = async () => {
    setBusy(true);
    setPhase("prep");
    try {
      await ensureWallet();
      toast.message("Preparing your claim…");
      const proof = await releaseMilestone(m.milestone_id);
      setPhase("wallet");
      toast.message("Confirm the transaction in your wallet…");
      await settleMilestoneOnchain(proof);
      setPhase("confirm");
      toast.message("Waiting for on-chain confirmation…");
      const ok = await pollConfirm(confirmClaimMilestone, m.milestone_id);
      if (ok) {
        toast.success("Payment claimed");
        reload();
      } else {
        toast.error("Claim not confirmed yet, refresh shortly");
      }
    } catch (e) {
      toast.error(e.shortMessage || e.response?.data?.detail || e.message || "Claim failed");
    } finally {
      setBusy(false);
      setPhase(null);
    }
  };

  const canDispute = caps.can_dispute;
  const linkedInvoice = m.invoice;
  const canGenerateInvoice = caps.can_invoice && !linkedInvoice;
  const disputeClaimant =
    m.dispute_resolution === "ReleaseToPayee"
      ? isPayee
      : m.dispute_resolution === "RefundToPayer"
        ? isPayer
        : isPayer || isPayee;
  const showClaim =
    (m.status === "Approved" && isPayee) ||
    (m.status === "Disputed" && m.dispute_resolved && disputeClaimant);

  const remindFund = async () => {
    try {
      await remindFunding(m.milestone_id);
      toast.success("Reminder sent to the payer");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const pendingRev = m.pending_revision;
  const editingRev = !!pendingRev && !pendingRev.approved;

  const openRevise = () => {
    if (editingRev) {
      setReviseAmount(String(pendingRev.amount));
      setReviseNote(pendingRev.note || "");
    } else {
      setReviseAmount("");
      setReviseNote("");
    }
    setReviseOpen(true);
  };

  const submitRevision = async () => {
    const amt = parseFloat(reviseAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    try {
      await proposeRevision(m.milestone_id, { amount: amt, note: reviseNote });
      toast.success(editingRev ? "Revision updated" : "Revision proposed");
      setReviseOpen(false);
      setReviseAmount("");
      setReviseNote("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const withdrawRev = async () => {
    try {
      await withdrawRevision(m.milestone_id);
      toast.success("Revision withdrawn");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const approveRev = async () => {
    try {
      await approveRevision(m.milestone_id);
      toast.success("Revision approved");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const declineRev = async () => {
    try {
      await declineRevision(m.milestone_id);
      toast.success("Revision declined");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const executeRevision = async () => {
    setBusy(true);
    setPhase("prep");
    try {
      await ensureWallet();
      toast.message("Preparing the on-chain adjustment…");
      const proof = await authorizeRevise(m.milestone_id);
      if (proof.applied) {
        toast.success("Milestone amount updated");
        reload();
        return;
      }
      setPhase("wallet");
      toast.message(
        proof.direction === "refund"
          ? "Confirm the refund in your wallet…"
          : "Confirm the top-up in your wallet…"
      );
      await reviseMilestoneOnchain(proof, ({ kind }) => setStep(kind));
      setPhase("confirm");
      toast.message("Waiting for on-chain confirmation…");
      const ok = await pollConfirm(confirmRevise, m.milestone_id);
      if (ok) {
        toast.success("Milestone amount updated");
        reload();
      } else toast.error("Revision not confirmed yet, refresh shortly");
    } catch (e) {
      toast.error(e.shortMessage || e.response?.data?.detail || e.message || "Revision failed");
    } finally {
      setBusy(false);
      setStep(null);
      setPhase(null);
    }
  };

  const cancelDisp = async () => {
    try {
      await cancelDispute(m.open_dispute.dispute_id);
      toast.success("Dispute cancelled");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const generateInvoice = async () => {
    try {
      const data = await invoiceFromMilestone(m.milestone_id);
      toast.success("Invoice generated & sent");
      reload();
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-5 border border-border rounded-xl" data-testid={`milestone-row-${m.milestone_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-semibold font-display">{m.title}</div>
            {(() => {
              const label = milestoneStatusLabel(m, contract);
              return (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor(label)}`}>
                  {label}
                </span>
              );
            })()}
          </div>

          {/* Unified state line */}
          {(() => {
            const pr = m.pending_revision;
            const Line = ({ icon: Icon = Clock, iconClass = "", children }) => (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon className={`w-3.5 h-3.5 shrink-0 ${iconClass}`} /> <span>{children}</span>
              </div>
            );
            if (pr && !pr.approved)
              return (
                <div data-testid={`revision-banner-${m.milestone_id}`}>
                  <Line icon={RotateCcw}>
                    Amount revision proposed:{" "}
                    <b className="font-semibold text-foreground">{fmtMoney(pr.amount, contract.currency)}</b>
                  </Line>
                  {pr.note && (
                    <ExpandableText
                      text={pr.note}
                      title="Revision note"
                      className="mt-0.5 pl-5 text-xs text-muted-foreground"
                      testid={`ms-revision-note-${m.milestone_id}`}
                    />
                  )}
                  {isPayer && <Line>Awaiting payee approval</Line>}
                  {isPayee && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        onClick={approveRev}
                        data-testid={`revise-approve-btn-${m.milestone_id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={declineRev}
                        data-testid={`revise-decline-btn-${m.milestone_id}`}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                </div>
              );
            if (pr && pr.approved)
              return (
                <div data-testid={`revision-banner-${m.milestone_id}`}>
                  <Line icon={CheckCircle} iconClass="text-primary">
                    Revision to{" "}
                    <b className="font-semibold text-foreground">{fmtMoney(pr.amount, contract.currency)}</b>{" "}
                    approved
                  </Line>
                  {isPayee && <Line>Waiting for the payer to execute</Line>}
                </div>
              );
            if (m.status === "Funded" && m.change_request_note)
              return (
                <div data-testid={`ms-state-${m.milestone_id}`}>
                  <Line icon={AlertTriangle} iconClass="text-amber-500">
                    Changes requested
                  </Line>
                  <ExpandableText
                    text={m.change_request_note}
                    title="Changes requested"
                    className="mt-0.5 pl-5 text-xs text-muted-foreground"
                    testid={`ms-change-note-${m.milestone_id}`}
                  />
                </div>
              );
            if (m.status === "Disputed") {
              const payeeName = isPayee ? "you" : contract.freelancer_name || contract.freelancer_email || "the payee";
              const payerName = isPayer ? "you" : contract.business_name || "the payer";
              const outcome = {
                ReleaseToPayee: `Released to ${payeeName}`,
                RefundToPayer: `Refunded to ${payerName}`,
                Split: "Split between both parties",
              }[m.dispute_resolution];
              return (
                <div data-testid={`ms-state-${m.milestone_id}`}>
                  <Line icon={AlertTriangle} iconClass="text-amber-500">
                    {m.dispute_resolved
                      ? outcome
                        ? `Dispute resolved. ${outcome}`
                        : "Dispute resolved"
                      : "Awaiting dispute resolution"}
                  </Line>
                </div>
              );
            }
            const line =
              m.status === "Funded"
                ? isPayee
                  ? "Ready for you to submit"
                  : "Awaiting submission"
                : m.status === "Submitted"
                  ? isPayee
                    ? "Waiting for approval"
                    : "Awaiting your review"
                  : m.status === "Approved"
                    ? isPayee
                      ? "Ready to claim"
                      : "Awaiting payee claim"
                    : null;
            if (!line) return null;
            return (
              <div data-testid={`ms-state-${m.milestone_id}`}>
                <Line>{line}</Line>
              </div>
            );
          })()}

          {m.notes && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">Brief</div>
              <ExpandableText
                text={m.notes}
                title="Brief"
                className="mt-0.5 text-sm text-muted-foreground"
                testid={`ms-notes-${m.milestone_id}`}
              />
            </div>
          )}
          {(m.deliverable_link || m.submit_notes) && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">Submission</div>
              {m.deliverable_link && (
                <div className="mt-0.5 text-sm">
                  <span className="text-muted-foreground">Deliverable: </span>
                  <a
                    href={m.deliverable_link}
                    target="_blank"
                    rel="noreferrer"
                    className="break-all text-primary hover:underline"
                  >
                    {m.deliverable_link}
                  </a>
                </div>
              )}
              {m.submit_notes && (
                <ExpandableText
                  text={m.submit_notes}
                  title="Submission note"
                  className="mt-0.5 text-sm text-muted-foreground"
                  testid={`ms-submit-notes-${m.milestone_id}`}
                />
              )}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-lg font-bold font-display">{fmtMoney(m.amount, contract.currency)}</div>
          {m.due_date && <div className="text-xs text-muted-foreground">Due {fmtDate(m.due_date)}</div>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        {caps.can_fund && (
          <Button
            size="sm"
            onClick={fundOnchain}
            disabled={busy}
            data-testid={`fund-onchain-btn-${m.milestone_id}`}
          >
            {busy ? (
              <Busy label={phase === "wallet" && step === "approve" ? "Approve in wallet…" : undefined} />
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-1" /> Fund escrow
              </>
            )}
          </Button>
        )}
        {caps.can_cancel && (
          <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`cancel-milestone-btn-${m.milestone_id}`}>
                <XCircle className="w-4 h-4 mr-1" /> Cancel milestone
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="cancel-milestone-modal">
              <DialogHeader>
                <DialogTitle>Cancel milestone</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  '{m.title}' will be marked Cancelled and stay visible in the contract's history. Both
                  parties are notified. This can't be undone.
                </p>
                <div>
                  <Label>Reason *</Label>
                  <Textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={3}
                    placeholder="Why is this milestone being cancelled?"
                    className="mt-1.5"
                    data-testid="cancel-milestone-reason-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={busy}>
                  Keep milestone
                </Button>
                <Button
                  variant="destructive"
                  onClick={submitCancel}
                  disabled={busy}
                  data-testid="cancel-milestone-confirm-btn"
                >
                  Cancel milestone
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {caps.can_remind_funding && (
          <Button
            size="sm"
            variant="outline"
            onClick={remindFund}
            data-testid={`remind-funding-btn-${m.milestone_id}`}
          >
            <Clock className="w-4 h-4 mr-1" /> Remind to fund
          </Button>
        )}
        {(caps.can_revise || (isPayer && editingRev)) && (
          <Dialog open={reviseOpen} onOpenChange={setReviseOpen}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={openRevise}
                data-testid={`revise-btn-${m.milestone_id}`}
              >
                <RotateCcw className="w-4 h-4 mr-1" /> {editingRev ? "Edit revision" : "Revise amount"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingRev ? "Edit proposed revision" : "Revise milestone amount"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  Current: {fmtMoney(m.amount, contract.currency)}. The payee approves before it takes effect
                  {m.status === "Funded" ? ", then you settle the on-chain difference" : ""}.
                </div>
                <div>
                  <Label>New amount *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={reviseAmount}
                    onChange={(e) => setReviseAmount(e.target.value)}
                    placeholder={String(m.amount)}
                    className="mt-1.5 h-11"
                    data-testid="revise-amount-input"
                  />
                </div>
                <div>
                  <Label>Note (optional)</Label>
                  <Textarea
                    value={reviseNote}
                    onChange={(e) => setReviseNote(e.target.value)}
                    rows={2}
                    placeholder="Why the change?"
                    className="mt-1.5"
                    data-testid="revise-note-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submitRevision} data-testid="revise-confirm-btn">
                  {editingRev ? "Update revision" : "Propose revision"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {isPayer && editingRev && (
          <Button
            size="sm"
            variant="ghost"
            onClick={withdrawRev}
            data-testid={`revise-withdraw-btn-${m.milestone_id}`}
          >
            Withdraw revision
          </Button>
        )}
        {isPayer &&
          m.pending_revision?.approved &&
          ["Funded", "Submitted", "Approved"].includes(m.status) && (
            <Button
              size="sm"
              onClick={executeRevision}
              disabled={busy}
              data-testid={`revise-execute-btn-${m.milestone_id}`}
            >
              {busy ? <Busy /> : "Execute revision"}
            </Button>
          )}
        {showClaim && (
          <Button
            size="sm"
            onClick={claimOnchain}
            disabled={busy}
            data-testid={`claim-btn-${m.milestone_id}`}
          >
            {busy ? (
              <Busy />
            ) : (
              <>
                <ArrowRight className="w-4 h-4 mr-1" /> Claim payment
              </>
            )}
          </Button>
        )}
        {m.open_dispute?.can_cancel && (
          <Button
            size="sm"
            variant="outline"
            onClick={cancelDisp}
            data-testid={`cancel-dispute-btn-${m.milestone_id}`}
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Cancel dispute
          </Button>
        )}
        {caps.can_submit && (
          <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid={`submit-btn-${m.milestone_id}`}>
                <ArrowRight className="w-4 h-4 mr-1" /> Submit work
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Submit work</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>What did you deliver? *</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    placeholder="Describe the work you delivered. Paste any links here."
                    className="mt-1.5"
                    data-testid="submit-notes-input"
                  />
                </div>
                <div>
                  <Label>Deliverable link (optional)</Label>
                  <Input
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    placeholder="https://…"
                    className="mt-1.5 h-11"
                    data-testid="submit-link-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={async () => {
                    if (!notes.trim()) {
                      toast.error("Add a note describing your work");
                      return;
                    }
                    await act("submit", { deliverable_link: link, notes });
                    setSubmitOpen(false);
                    setLink("");
                    setNotes("");
                  }}
                  data-testid="submit-confirm-btn"
                >
                  Submit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {caps.can_approve && (
          <>
            <Button size="sm" onClick={() => act("approve")} data-testid={`approve-btn-${m.milestone_id}`}>
              <CheckCircle className="w-4 h-4 mr-1" />
              {linkedInvoice ? " Approve invoice" : " Approve"}
            </Button>
            <Dialog open={changesOpen} onOpenChange={setChangesOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid={`request-changes-btn-${m.milestone_id}`}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Request changes
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request changes</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Label>What needs changing?</Label>
                  <Textarea
                    value={changeNote}
                    onChange={(e) => setChangeNote(e.target.value)}
                    rows={4}
                    placeholder="Be specific…"
                    className="mt-1.5"
                    data-testid="request-changes-note-input"
                  />
                </div>
                <DialogFooter>
                  <Button
                    onClick={async () => {
                      await act("request-changes", { note: changeNote });
                      setChangesOpen(false);
                      setChangeNote("");
                    }}
                    data-testid="request-changes-confirm-btn"
                  >
                    Send back to payee
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
        {canGenerateInvoice && (
          <Button
            size="sm"
            variant="outline"
            onClick={generateInvoice}
            data-testid={`generate-invoice-btn-${m.milestone_id}`}
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Generate invoice
          </Button>
        )}
        {linkedInvoice && (
          <Link to={`/invoices/${linkedInvoice.invoice_id}`}>
            <Button size="sm" variant="outline" data-testid={`view-invoice-btn-${m.milestone_id}`}>
              <FileSpreadsheet className="w-4 h-4 mr-1" /> View invoice
            </Button>
          </Link>
        )}
        {canDispute && (
          <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`dispute-btn-${m.milestone_id}`}>
                <AlertTriangle className="w-4 h-4 mr-1" /> File a dispute
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>File a dispute</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  {contract.title} · {m.title} · {fmtMoney(m.amount, contract.currency)}
                </div>
                <div>
                  <Label>Reason *</Label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring"
                    data-testid="dispute-reason-select"
                  >
                    <option value="">Select a reason…</option>
                    <option value="Work not delivered">Work not delivered</option>
                    <option value="Quality issue">Quality issue</option>
                    <option value="Scope mismatch">Scope mismatch</option>
                    <option value="Late delivery">Late delivery</option>
                    <option value="Non-payment">Non-payment (payer didn't release)</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Details *</Label>
                  <Textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    rows={3}
                    className="mt-1.5"
                    placeholder="Give specifics so a reviewer can act fast."
                    data-testid="dispute-details-input"
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Amount in dispute</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={disputeAmt}
                      onChange={(e) => setDisputeAmt(e.target.value)}
                      placeholder={String(m.amount)}
                      className="mt-1.5 h-11"
                      data-testid="dispute-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Desired outcome</Label>
                    <select
                      value={desiredOutcome}
                      onChange={(e) => setDesiredOutcome(e.target.value)}
                      className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring"
                      data-testid="dispute-outcome-select"
                    >
                      <option value="ReleaseToPayee">
                        Release funds to{" "}
                        {isPayee
                          ? "me"
                          : contract.freelancer_name || contract.freelancer_email || "the payee"}
                      </option>
                      <option value="RefundToPayer">
                        Refund to {isPayer ? "me" : contract.business_name || "the payer"}
                      </option>
                      <option value="Split">Split between both parties</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Evidence link (optional)</Label>
                  <Input
                    value={evidence}
                    onChange={(e) => setEvidence(e.target.value)}
                    placeholder="https://drive.google.com/…"
                    className="mt-1.5 h-11"
                    data-testid="dispute-evidence-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={async () => {
                    if (!reason) {
                      toast.error("Reason required");
                      return;
                    }
                    if (!details) {
                      toast.error("Details required");
                      return;
                    }
                    await act("dispute", {
                      reason,
                      details,
                      amount_in_dispute: disputeAmt ? parseFloat(disputeAmt) : null,
                      desired_outcome: desiredOutcome,
                      evidence_link: evidence || null,
                    });
                    setDisputeOpen(false);
                    setReason("");
                    setDetails("");
                    setDisputeAmt("");
                    setDesiredOutcome("Other");
                    setEvidence("");
                  }}
                  data-testid="dispute-confirm-btn"
                >
                  File dispute
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default MilestoneRow;
